import moment from 'moment';
import {
  ALGOLIA_SEARCH_RESULTS,
  onEvent,
  SHOW_HIDDEN_TEASER,
  triggerEvent,
} from '@fec/assets/js/utils/event';
import { camelCase } from 'lodash';

export const getVisibleFilters = (filters) =>
  filters.filter((filter) => !filter.hidden);

export const getQuerystringFromParams = (params) => {
  let urlParams = new URLSearchParams(params);
  return urlParams.toString();
};

export const getParamsFromQuerystring = () => {
  let result = {};
  let urlParams = new URLSearchParams(window.location.search);
  for (let entry of urlParams.entries()) {
    result[entry[0]] = entry[1];
  }
  return result;
};

export const updateOrSetQuerystringFromParams = (params) => {
  let prevParams = getParamsFromQuerystring();
  if (prevParams.dateFrom) {
    delete prevParams.dateFrom;
  }

  if (prevParams.dateTo) {
    delete prevParams.dateTo;
  }

  let query = new URLSearchParams({ ...prevParams, ...params });

  if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
    // only make history entry if state and query not already equal
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${query.toString()}`,
    );
  }

  return query.toString();
};

export const getDefaultSearchParams = (onlyVisible = false, configFilters) => {
  const visibleFilters = configFilters.filter((filter) => !filter.hidden);
  let searchParams = {
    q: '',
    date: 'all',
    page: 0,
  };

  let filters = onlyVisible ? visibleFilters : configFilters;

  filters.map((filter) => {
    searchParams[filter.name] = filter.selectedOption;
  });

  return searchParams;
};

// Used to format with moment.js
const TIMESTAMP = 'X';
export const DATEFORMAT = 'YYYY-MM-DD';

/**
 * get correct date from Moment.js and format it for each search
 *
 * @param count
 * @param unitOfTime
 * @param format
 * @returns {string}
 */
function getTime(count, unitOfTime, format) {
  return moment().subtract(count, unitOfTime).format(format);
}

export function getFilterValue() {
  return {
    day: {
      algolia: moment().startOf('day').format(TIMESTAMP),
      play: moment().startOf('day').format(DATEFORMAT),
    },
    threeDays: {
      algolia: getTime(3, 'days', TIMESTAMP),
      play: getTime(3, 'days', DATEFORMAT),
    },
    week: {
      algolia: getTime(1, 'week', TIMESTAMP),
      play: getTime(1, 'week', DATEFORMAT),
    },
    month: {
      algolia: getTime(1, 'month', TIMESTAMP),
      play: getTime(1, 'month', DATEFORMAT),
    },
    sixMonths: {
      algolia: getTime(6, 'months', TIMESTAMP),
      play: getTime(6, 'months', DATEFORMAT),
      //label: translation.sixMonths
    },
    year: {
      algolia: getTime(1, 'year', TIMESTAMP),
      play: getTime(1, 'year', DATEFORMAT),
    },
    range: {
      algolia: 'range',
      play: 'range',
    },
    all: { algolia: 'all', play: 'all' },
  };
}

export function translate(searchConfig, translations, key) {
  return key in searchConfig
    ? searchConfig[key] // string from search-config
    : translations[key]
      ? translations[key] // translation
      : translations[camelCase(key)]
        ? translations[camelCase(key)] // translation for camelCased-key (e.g. allows overriding config-labels with translations when necessary)
        : key;
}

export const getTrackingInformation = (
  portalName,
  filters,
  searchParams,
  searchName,
) => {
  let source =
    portalName === 'all' ? 'search_result' : 'search_rubric_' + portalName;

  // Defensive programming, only apply name in case of audio config
  if (searchName && searchName === 'audio') {
    source = 'search_audio';
  }

  let filterStr = '';
  if (filters.length) {
    filterStr = getVisibleFilters(filters) // only visible filters should be tracked.
      // exclude non-existing names in searchParams and unsapplied filters ('all')
      .filter(
        (filter) =>
          searchParams[filter.name] && searchParams[filter.name] !== 'all',
      )
      .reduce(
        (acc, filter) =>
          acc + filter.name + ',' + searchParams[filter.name] + ' ',
        '',
      );
  }

  return {
    filters: filterStr ? 'Filter:' + filterStr.slice(0, -1) : '0',
    source: source,
  };
};

export function getFilterValueByKey(key) {
  return getFilterValue()[key] ? getFilterValue()[key] : getFilterValue['all'];
}

// subscribes to the search event
export function initFallbackCollectionListener(fallbackCollectionId) {
  if (!fallbackCollectionId) {
    return;
  }

  let el = document.querySelector(
    `[data-urn="urn:srf:collection:${fallbackCollectionId}"]`,
  );

  if (el) {
    onEvent({
      eventName: ALGOLIA_SEARCH_RESULTS,
      eventHandler: ({ detail }) => {
        const showFallback = detail?.algoliaResponse.nbHits === 0;

        el.style.display = showFallback ? 'grid' : 'none';

        if (showFallback) {
          triggerEvent(SHOW_HIDDEN_TEASER);
        }
      },
    });
  }
}
